/**
 * IndexedDB-backed local mirror of cloud-sync state.
 *
 * Map<itemKind, { manifest, blob, lastSyncedTs }>. One record per itemKind
 * (settings, contactRoster, blockList) — each kind has at most one
 * canonical manifest at any time.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/CloudSyncStore.swift` (the iOS store
 * is file-per-(kind,itemId); the web spec collapses to one record per
 * itemKind because the web feature scope is the three closed kinds only).
 *
 * THREAT MODEL
 *   - Both the Service Worker (push handler, future reconcile-loop) and
 *     the main thread (settings UI, contact roster edits) touch these
 *     records. Every read-modify-write runs inside an exclusive IDB
 *     `readwrite` transaction so concurrent writes can't interleave and
 *     overwrite each other.
 *   - IndexedDB has no `.completeFileProtection` equivalent on the web.
 *     The blob is already AES-GCM-sealed before it reaches this store, so
 *     a device-lock leak only reveals the sealed envelope — the same
 *     trade-off documented in `Signal_standard_web.md` row
 *     "`.completeFileProtection`".
 *   - The store holds NO key material. The cloud root key, sync keys, and
 *     identity private keys live elsewhere; this file only persists
 *     opaque sealed blobs + their signed manifests.
 *   - Two `CloudSyncStore` instances pointing at the same `dbName` share
 *     state via the IDB origin. This is the SW/main sharing path.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs.
 *   Only `indexedDB.*` and value-type imports.
 *
 * @module CloudSyncStore
 */

import type {
  CloudSyncItemKind,
  CloudSyncManifest,
} from "./CloudSyncRecord";

// ---------------------------------------------------------------------------
// IDB wiring
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "cloud-sync";
const DEFAULT_DB_NAME = "kaether:cloud-sync:v1";

/**
 * On-disk shape. The manifest is plain numbers + a 32-byte Uint8Array; IDB
 * structured-clone supports both natively so we don't need to base64 here.
 *
 * `signature` is included because a verified signature is what justifies
 * persisting the record in the first place. Storing it lets later reads
 * re-verify if the caller wants to (e.g. on a re-key event).
 *
 * `lastSyncedTs` records the unix ms at which we last reconciled this
 * record with the cloud. Used by the reconcile loop (not yet wired) to
 * order pulls.
 */
interface PersistedRecord {
  readonly manifest: CloudSyncManifest;
  readonly blob: Uint8Array;
  readonly signature: Uint8Array;
  readonly lastSyncedTs: number;
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function readRecord(
  db: IDBDatabase,
  key: CloudSyncItemKind,
): Promise<PersistedRecord | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(key);
    req.onsuccess = () => {
      const val = req.result as PersistedRecord | undefined;
      resolve(val ?? null);
    };
    req.onerror = () => reject(req.error);
  });
}

function writeRecord(
  db: IDBDatabase,
  key: CloudSyncItemKind,
  rec: PersistedRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(rec, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function deleteRecord(
  db: IDBDatabase,
  key: CloudSyncItemKind,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------------------------------------------------------------------------
// CloudSyncStore
// ---------------------------------------------------------------------------

/**
 * Persistent map of cloud-sync state, one record per `CloudSyncItemKind`.
 *
 * Two instances pointing at the same `dbName` share state — this is the
 * SW/main thread sharing path. The DEFAULT_DB_NAME singleton is suitable
 * for production; the self-test supplies a fresh DB name per case.
 */
export class CloudSyncStore {
  private static _shared: CloudSyncStore | null = null;

  /** Lazy-initialised default-DB singleton, suitable for production use. */
  static get shared(): CloudSyncStore {
    if (!CloudSyncStore._shared) {
      CloudSyncStore._shared = new CloudSyncStore();
    }
    return CloudSyncStore._shared;
  }

  private readonly dbName: string;
  private _db: IDBDatabase | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  private async db(): Promise<IDBDatabase> {
    if (!this._db) {
      this._db = await openDb(this.dbName);
    }
    return this._db;
  }

  // --------------------------------------------------------------------------
  // Read
  // --------------------------------------------------------------------------

  /**
   * Current manifest for `kind`, or null if we've never observed one. Does
   * NOT verify the signature — the on-disk record was either signed by us
   * (recordLocal) or arrived through applyRemote, which already verified.
   */
  async currentManifest(
    kind: CloudSyncItemKind,
  ): Promise<CloudSyncManifest | null> {
    const db = await this.db();
    const rec = await readRecord(db, kind);
    return rec?.manifest ?? null;
  }

  /** Current sealed blob bytes for `kind`, or null if absent. */
  async currentBlob(kind: CloudSyncItemKind): Promise<Uint8Array | null> {
    const db = await this.db();
    const rec = await readRecord(db, kind);
    return rec ? rec.blob : null;
  }

  /** Current Ed25519 signature for `kind`, or null if absent. */
  async currentSignature(
    kind: CloudSyncItemKind,
  ): Promise<Uint8Array | null> {
    const db = await this.db();
    const rec = await readRecord(db, kind);
    return rec ? rec.signature : null;
  }

  // --------------------------------------------------------------------------
  // Write (remote origin)
  // --------------------------------------------------------------------------

  /**
   * Apply a remote-origin manifest+blob to the local mirror, replacing the
   * current record if any. The CALLER must have already verified the
   * signature (via `openItem` in `CloudSyncCipher`) and resolved any
   * conflict (via `resolveConflict` in `CloudSyncConflictPolicy`) before
   * calling this; this method is the persist-the-winner sink, NOT the
   * policy boundary.
   *
   * @param kind         Item kind.
   * @param manifest     Verified manifest.
   * @param blob         Sealed blob bytes.
   * @param signature    64-byte Ed25519 signature over the canonical bytes.
   * @param lastSyncedTs Unix ms reconciliation timestamp.
   */
  async applyRemote(
    kind: CloudSyncItemKind,
    manifest: CloudSyncManifest,
    blob: Uint8Array,
    signature: Uint8Array,
    lastSyncedTs: number = Date.now(),
  ): Promise<void> {
    if (manifest.itemKind !== kind) {
      throw new RangeError(
        `applyRemote: manifest.itemKind=${manifest.itemKind} != kind=${kind}`,
      );
    }
    const db = await this.db();
    await writeRecord(db, kind, {
      manifest,
      blob,
      signature,
      lastSyncedTs,
    });
  }

  // --------------------------------------------------------------------------
  // Write (local origin)
  // --------------------------------------------------------------------------

  /**
   * Persist a locally-authored manifest+blob. Equivalent to applyRemote in
   * persistence semantics — the only reason it's a separate entry point is
   * intent clarity: the caller has produced this record via `sealItem` and
   * is about to push it to KDS; the local-mirror persistence is what
   * survives the device-side `applyRemote` of the same record arriving
   * back from the reconcile loop.
   */
  async recordLocal(
    kind: CloudSyncItemKind,
    manifest: CloudSyncManifest,
    blob: Uint8Array,
    signature: Uint8Array,
    lastSyncedTs: number = Date.now(),
  ): Promise<void> {
    if (manifest.itemKind !== kind) {
      throw new RangeError(
        `recordLocal: manifest.itemKind=${manifest.itemKind} != kind=${kind}`,
      );
    }
    const db = await this.db();
    await writeRecord(db, kind, {
      manifest,
      blob,
      signature,
      lastSyncedTs,
    });
  }

  // --------------------------------------------------------------------------
  // Clear
  // --------------------------------------------------------------------------

  /**
   * Remove the on-disk record for `kind`. Used by reset-device flows and
   * by self-test fixtures. Does NOT touch the cloud — the caller is
   * responsible for telling KDS to wipe (`DELETE /users/me/cloudsync/{kind}`).
   */
  async clear(kind: CloudSyncItemKind): Promise<void> {
    const db = await this.db();
    await deleteRecord(db, kind);
  }
}
